import React from 'react';
import { addClassNames } from '../../Functions/Helper/Component';
import { GripHorizontal as GripIcon, ChevronUp as UpIcon, ChevronDown as DownIcon } from './Icons';
import Draggable from './Draggable';
import Droppable from './Droppable';
import './Orderable.scss';

function DroppableAction({index, typeSymbol, onChange, className}) {
    return (
        <Droppable
            type={typeSymbol}
            className={addClassNames(
                "w-100 b-2 border-primary active-height",
                (className || "")
            )}
            onDrop={({data: drop}) => {
                if (typeof onChange === "function") onChange((values) => {
                    values = values || [];
                    if (
                        isNaN(drop?.index)
                        || (drop?.index === index) 
                        || drop.index < 0
                    ) return values;
                    
                    const newValues = [...(values || [])];
                    const [value] = newValues.splice(drop.index, 1);

                    newValues.splice(
                        index > drop.index? index - 1
                        : index, 0, value
                    );
                    return newValues;
                });
            }}
        />
    );
}

function DraggableDisplay({
    ItemComponent, typeSymbol, containerRef,
    draggableClassName, value, index, locked,
    lock, unlock, onChange, dragging, className, 
    moveUp, moveDown, ...props
}) {
    const draggableItem = React.useRef(null);
    const gripRef = React.useRef(null);
    const item = (
        <div
            className="item-view d-flex justify-content-between gap-2 align-items-center w-100 flex-grow-1"
        >
            <ItemComponent
                index={index}
                value={value}
                onChange={onChange}
                className={addClassNames(className, `array-item item-${index}`)}
                lock={lock}
                unlock={unlock}
                {...props}
            />
        </div>
    );
    if (locked) {
        return item;
    } else {
        return (
            <React.Fragment>
                <div ref={draggableItem}>
                <DroppableAction 
                    index={index}
                    typeSymbol={typeSymbol}
                    onChange={onChange}
                    className={addClassNames(`array-reorder-space mb-3 droppable-item-${index}`)}
                />

                <Draggable
                    type={typeSymbol}
                    data={{ index, value }}
                    onDragStop={() => {
                        // any rapid change to up when dropping start results in drop being executed wrongly
                        setTimeout(() => {
                            if (draggableItem.current) draggableItem.current.classList.remove("d-none");
                            if (containerRef.current) containerRef.current.classList.remove("dragging");
                        }, 15);
                        
                    }}
                    onDragStart={() => { 
                        // any rapid change to up when drag start results in the drag not starting
                        setTimeout(() => {
                            if (draggableItem.current) draggableItem.current.classList.add("d-none");
                            if (containerRef.current) containerRef.current.classList.add("dragging");
                        }, 15);
                    }}
                    gripRef={gripRef}
                    className={addClassNames("display-view card text-bg-light px-3 py-3 pb-4 d-flex justify-content-start align-items-center flex-wrap flex-sm-wrap flex-md-wrap flex-lg-nowrap flex-xl-nowrap flex-xxl-nowrap  gap-2 position-relative", draggableClassName)}
                > <div className="d-flex flex-row flex-nowrap align-items-center gap-2 justify-content-center">

                        {index !== 0 && (
                            <button
                                type="button"
                                className="btn outline-none shadow-none color-inherit p-0"
                                onClick={(e)=> {
                                    e.preventDefault();
                                    moveUp(index);
                                }}
                            ><UpIcon className="fs-5" /></button>
                        )}
                        <GripIcon ref={gripRef} className="fs-4"/>
                        {index + 1 <= props.arr.length && (
                            <button
                                type="button"
                                className="btn outline-none shadow-none color-inherit p-0"
                                onClick={(e) => {
                                    e.preventDefault();
                                    moveDown(index);
                                }}
                            ><DownIcon className="fs-5" /></button>
                        )}
                    
                    </div>
                    {item}
                </Draggable>
                </div>
            </React.Fragment>
        );
    }
}

export default function Orderable({ 
    ItemComponent,
    values,
    onChange,
    keyFunction,
    draggableClassName,
    draggableContainerClassName,
    className,
    ...props
}) {
    const typeSymbol = React.useMemo(() => Symbol("orderable-item"), []);
    const [locked, setLocked] = React.useState({});
    const containerRef = React.useRef(null);

    return (
        <div
            ref={containerRef}
            className={addClassNames("droppable-dragging-visible orderable-container gap-3 d-flex flex-column", draggableContainerClassName)}
        >
            {(values || []).map((value, index) => {
                const key = value?.key || (
                    (typeof keyFunction === "function" && keyFunction(value, index) ) ||  `item-${index}`
                );

                return (
                    <DraggableDisplay
                        key={key}
                        value={value}
                        arr={values}
                        index={index}
                        locked={locked[index]}
                        lock={() => setLocked(locked => ({...locked, [index]: true}))}
                        unlock={() => setLocked(locked => ({...locked, [index]: false}))}
                        onChange={onChange}
                        ItemComponent={ItemComponent}
                        typeSymbol={typeSymbol}
                        containerRef={containerRef}
                        draggableClassName={draggableClassName}
                        {...props}
                    />
                )
            })}
            <DroppableAction 
                index={values.length}
                typeSymbol={typeSymbol}
                onChange={onChange}
                key={"droppable-final-item"}
                className={`array-reorder-space droppable-item-${values.length}`}
            />
        </div>
    );
}